import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from "../assets/images/logo.png";
import "../components/sidebar.css"; // Ensure this CSS file is updated

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [userRole, setUserRole] = useState(null); // State to store user role
  const location = useLocation();

  // Get user role from localStorage once on component mount
  useEffect(() => {
    setUserRole(localStorage.getItem('role'));
  }, []);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  // Close sidebar when route changes on mobile
  useEffect(() => {
    const handleRouteChange = () => {
      if (window.innerWidth < 992) {
        setIsOpen(false);
      }
    };
    handleRouteChange(); // Call once on mount for initial state based on route
    // The effect already runs on location.pathname change, so this might be redundant if you link externally
    // However, keeping it tied to location.pathname explicitly is good if direct URL changes are common.
  }, [location.pathname]);

  // Handle window resize to auto-open sidebar on desktop and close on mobile initially
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setIsOpen(true); // Always open on desktop
      } else {
        setIsOpen(false); // Close on mobile initially
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once on mount to set initial state

    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty dependency array means this runs only once on mount and cleans up on unmount

  const getLinkClass = (path) => {
    return `sidebar-link d-flex align-items-center ${location.pathname === path ? 'active' : ''}`;
  };

  return (
    <>
      {/* Mobile toggle button */}
      <div className="mobile-toggle-container d-lg-none p-3">
        <button className="btn btn-primary toggle-button" onClick={toggleSidebar} aria-label="Toggle sidebar">
          <i className={`fas ${isOpen ? 'fa-times' : 'fa-bars'}`}></i>
        </button>
      </div>

      {/* Sidebar */}
      <div
        className={`sidebar bg-white position-fixed top-0 start-0 h-100 shadow-sm p-4 transition-all ${isOpen ? 'open' : 'closed'} d-lg-block`}
        style={{ width: '280px', zIndex: 1050 }}
      >
        <div className="brand-logo text-center">
          <Link to="/dashboard" className="text-nowrap logo-img d-block">
            <img src={logo} width={180} alt="Logo" className="img-fluid" />
          </Link>
        </div>

        <nav className="sidebar-nav scroll-sidebar" data-simplebar>
          <ul className="list-unstyled">
            {userRole === 'admin' && (
              <li className="mb-3">
                <Link className={getLinkClass("/members")} to="/members">
                  <i className="fas fa-users icon"></i>
                  <span>Members</span>
                </Link>
              </li>
            )}

            <li className="mb-3">
              <Link className={getLinkClass("/linkedmembers")} to="/linkedmembers">
                <i className="fas fa-user-friends icon"></i>
                <span>Linked Members</span>
              </Link>
            </li>

            {userRole === 'admin' && (
              <li className="mb-3">
                <Link className={getLinkClass("/membershiptypes")} to="/membershiptypes">
                  <i className="fas fa-id-badge icon"></i>
                  <span>Membership Types</span>
                </Link>
              </li>
            )}

            {userRole === 'admin' && (
              <li className="mb-3">
                <Link className={getLinkClass("/membershippayments")} to="/membershippayments">
                  <i className="fas fa-credit-card icon"></i>
                  <span>Membership Payments</span>
                </Link>
              </li>
            )}

            {userRole === 'user' && (
              <li className="mb-3">
                <Link className={getLinkClass("/membershippayments")} to="/membershippayments">
                  <i className="fas fa-credit-card icon"></i>
                  <span>My Payments</span>
                </Link>
              </li>
            )}

            {userRole === 'admin' && (
              <li className="mb-3">
                <Link className={getLinkClass("/membershippaymentsbydate")} to="/membershippaymentsbydate">
                  <i className="fas fa-calendar-alt icon"></i>
                  <span>Payments By Date</span>
                </Link>
              </li>
            )}

            {userRole === 'admin' && (
              <li className="mb-3">
                <Link className={getLinkClass("/eventsall")} to="/eventsall">
                  <i className="fas fa-calendar-check icon"></i>
                  <span>Events</span>
                </Link>
              </li>
            )}

            {userRole === 'admin' && (
              <li className="mb-3">
                <Link className={getLinkClass("/paypaltransactions")} to="/paypaltransactions">
                  <i className="fas fa-receipt icon"></i> {/* Changed to a more fitting icon */}
                  <span>Paypal Transactions</span>
                </Link>
              </li>
            )}

            {userRole === 'user' && (
              <li className="mb-3">
                <Link className={getLinkClass("/myeventregistration")} to="/myeventregistration">
                  <i className="fas fa-calendar-check icon"></i>
                  <span>Event Registrations</span>
                </Link>
              </li>
            )}

            {/* Note: The original code had 'member' role here.
                If 'user' covers 'member' functionalities for payments,
                this might be redundant or require clarification on roles.
                Assuming 'user' is the general member role now. */}
            {/* {userRole === 'member' && (
              <li className="mb-3">
                <Link className={getLinkClass("/mypayments")} to="/mypayments">
                  <i className="fas fa-receipt icon"></i>
                  <span>My Payments</span>
                </Link>
              </li>
            )} */}
          </ul>
        </nav>
      </div>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="sidebar-overlay position-fixed top-0 start-0 w-100 h-100 bg-dark opacity-50 d-lg-none"
          style={{ zIndex: 1040 }}
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        ></div>
      )}
    </>
  );
};

export default Sidebar;